/* Filtros globales del Overview: sucursales, grupos, eventos y fechas.
   Combinación: OR dentro de una dimensión, AND entre dimensiones.
   - Sucursales: las marcadas, o las de los grupos seleccionados (con grupos seleccionados solo cuentan los grupos).
   - Eventos: unión de los eventos seleccionados.
   - Fechas: un único rango.
   Las tres dimensiones se cruzan con AND. */

/* /dashboard-metrics/resolve filtra por grupos y eventos desde la Fase 3. Si este valor se pusiera en false, el Overview
   volvería a NO pedir métricas con grupos o eventos seleccionados: mostrar las cifras generales como si estuvieran
   filtradas sería un dato incorrecto. */
export const METRICS_SUPPORT_GROUP_EVENT_FILTERS = true;

export const UNSUPPORTED_FILTER_MESSAGE =
  'Los filtros de grupos y eventos todavía no se aplican a las métricas. No se muestran cifras para no presentar datos sin filtrar.';

/* Defensa adicional: con grupos o eventos seleccionados solo se muestran cifras si la respuesta confirma (`scope.applied`)
   que el servidor aplicó esos filtros. Un servidor anterior los ignoraría y devolvería cifras generales. */
export const UNCONFIRMED_FILTER_MESSAGE =
  'El servidor no confirmó que aplicó los filtros de grupos y eventos. No se muestran cifras para no presentar datos sin filtrar.';

export const scopeConfirmsFilters = (scope) => Boolean(scope && scope.applied === true);

// true cuando hay grupos o eventos seleccionados y las métricas aún no pueden filtrarlos
export const shouldBlockMetrics = (groupIds, eventIds, supported = METRICS_SUPPORT_GROUP_EVENT_FILTERS) =>
  !supported && ((groupIds || []).length > 0 || (eventIds || []).length > 0);

const uniq = (items) => Array.from(new Set(items));

const cleanGroupIds = (value) => (Array.isArray(value)
  ? uniq(value.filter((id) => typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)))
  : []);

const cleanEventIds = (value) => (Array.isArray(value)
  ? uniq(value.map(Number).filter((id) => Number.isInteger(id) && id > 0))
  : []);

// Selecciones guardadas en los filtros del dashboard. Los dashboards anteriores no tienen estos campos: quedan vacías.
export const readSavedSelections = (filters) => ({
  groupIds: cleanGroupIds(filters?.group_ids),
  eventIds: cleanEventIds(filters?.event_ids),
});

// Filtros que se guardan con el dashboard
export const buildSavedFilters = ({ branchCodes, metricFilters, groupIds, eventIds }) => ({
  branchoffice_code: branchCodes,
  ...metricFilters,
  group_ids: cleanGroupIds(groupIds),
  event_ids: cleanEventIds(eventIds),
});

export const toggleValue = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

/* Quita de la selección guardada lo que ya no existe (grupo desactivado, evento borrado).
   Solo se reconcilia si el catálogo se cargó bien: si la carga falló no se descarta nada. */
export const reconcileSelection = (selected, availableIds, catalogLoaded) => {
  if (!catalogLoaded) return { kept: selected, dropped: [] };
  const available = new Set(availableIds.map(String));
  return {
    kept: selected.filter((id) => available.has(String(id))),
    dropped: selected.filter((id) => !available.has(String(id))),
  };
};

// Sucursales de un grupo que existen realmente (las que ya no existen se muestran, pero no cuentan)
export const getGroupAvailableBranches = (group) => (group?.branches || []).filter((branch) => !branch.missing);

/* Sucursales que aportan los grupos seleccionados, sin repetir las que están en varios grupos.
   `emptyGroups` son grupos seleccionados que no tienen ninguna sucursal disponible. */
export const resolveSelectedGroupBranches = (groups, selectedIds) => {
  const wanted = new Set((selectedIds || []).map(String));
  const byCode = new Map();
  const emptyGroups = [];

  (groups || []).filter((group) => wanted.has(String(group.id))).forEach((group) => {
    const available = getGroupAvailableBranches(group);
    if (available.length === 0) emptyGroups.push(group);
    available.forEach((branch) => {
      if (!byCode.has(branch.code)) byCode.set(branch.code, { code: branch.code, name: branch.name });
    });
  });

  return { branches: Array.from(byCode.values()), emptyGroups };
};

/* Alcance de sucursales que se enviará a las métricas.
   Sin grupos se conserva el comportamiento actual. Con grupos seleccionados solo cuentan los grupos: las sucursales
   marcadas individualmente se ocultan en la pantalla y no se envían (vuelven al quitar los grupos). */
export const buildBranchScope = ({ selectedBranchCodes, groupIds }) => {
  if ((groupIds || []).length === 0) {
    return { branchoffice_code: selectedBranchCodes, group_ids: [] };
  }
  return { branchoffice_code: [], group_ids: groupIds };
};
